import { AxiosResponse } from 'axios';
import { HttpResponse } from 'shell/models/http-response';
import { ApplicantData, PersonData, PracticeData } from 'shell/models/practice';
import { DateTime } from 'luxon';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';
import { AccordionGeneralityForm as GeneralityVM } from 'shell/view-models/accordion-generality-form';
import { CivilStatusID } from 'shell/constants/civil-status';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';
import { TypeInitiative } from 'practice/constants/practice-management-step';
// import { CitizenshipVM } from 'shell/view-models/citizenship';

const transformGeneralityOffice = (data: PersonData): GeneralityVM => {
  const birthChoiceDate = transformToChoiceDate(data.senzaGiorno, data.senzaGiornoMese);

  return {
    fiscalCode: data.codFiscale || '',
    birthChoiceDate,
    // validityFiscalCodeId: TODO campo mancante
    // validityDateFiscalCode: TODO campo mancante
    aire: data.flagAire || 'N',
    name: data.nome || '',
    surname: data.cognome || '',
    gender: data.sesso || '',
    withoutName: !Boolean(data.nome),
    withoutSurname: !Boolean(data.cognome),
    birthDate: data.dataNascita ? DateTime.fromISO(data.dataNascita) : null,
    italian: {
      municipality: Boolean(data.comuneVO?.provinciaVO)
        ? ({
            id: data.comuneVO?.codIstat,
            description: data.comuneVO?.nome,
            province: data.comuneVO?.provincia,
            istatCode: data.comuneVO?.codIstat,
          } as MunicipalityAnprVM)
        : null,
      // location: data.comuneAnprVO?.descrizioneLocalita TODO campo mancante
      province: data.comuneAnprVO?.codiceSiglaProvincia,
    },
    foreign: {
      municipality: !Boolean(data.comuneVO?.provinciaVO)
        ? {
            id: data.comuneVO?.codIstat || '',
            description: data.comuneVO?.nome || '',
            province: data.comuneVO?.provincia || '',
            istatCode: data.comuneVO?.codIstat || '',
          }
        : null,
      state: {
        id:
          data.statoEsteroVO?.codiceIstat?.toString() ||
          data.statoEsteroAnprVO?.codiceIstat?.toString() ||
          data.comuneVO?.statoEsteroVO?.codiceIstat?.toString() ||
          '',
        value:
          data.statoEsteroVO?.denominazione ||
          data.statoEsteroAnprVO?.denominazione ||
          data.comuneVO?.statoEsteroVO?.nome ||
          '',
        province:
          data.statoEsteroVO?.codiceIso ||
          data.statoEsteroAnprVO?.codiceIso ||
          data.comuneVO?.statoEsteroVO?.iso ||
          '',
        istatCode:
          data.statoEsteroVO?.codiceIstat ||
          data.statoEsteroAnprVO?.codiceIstat ||
          data.comuneVO?.statoEsteroVO?.codiceIstat ||
          '',
      },
      province: data.statoEsteroVO?.codiceSiglaProvincia,
    },
    isForeign: !Boolean(data.comuneVO?.provinciaVO),
    // exceptionalBirthPlace: TODO mancante
    municipalityIdentifier: data.key?.idDbLocalePersona?.toString(),
    // istatCode: TODO idSchedaSoggettoComune -> idSchedaSoggettoComuneIstat
    anprIdentifier: data.key?.idAnprPersona,
    note: data.note,
  };
};

const transformGeneralityRichiedenteOffice = (data: ApplicantData): GeneralityVM => {
  const birthChoiceDate = transformToChoiceDate(); // TODO non ci sono in tabella

  return {
    fiscalCode: data.codFiscaleRichiedente || '',
    //birthChoiceDate,
    // validityFiscalCodeId: TODO campo mancante
    // validityDateFiscalCode: TODO campo mancante
    //aire: data.flagAire || undefined, TODO non presente in tabella
    name: data.nome || '',
    surname: data.cognome || '',
    gender: data.sesso || '',
    withoutName: !Boolean(data.nome),
    withoutSurname: !Boolean(data.cognome),
    birthDate: data.dataNascita ? DateTime.fromISO(data.dataNascita) : null,
    birthChoiceDate,
    italian: {
      municipality: {
        id: data.comuneAnprVO?.key?.codice,
        description: data.comuneAnprVO?.denominazione,
        province: data.comuneAnprVO?.codiceSiglaProvincia,
        istatCode: data.comuneAnprVO?.codiceIstat,
      } as MunicipalityAnprVM,
      // location: data.comuneAnprVO?.descrizioneLocalita TODO campo mancante
      province: data.comuneAnprVO?.codiceSiglaProvincia,
    },
    foreign: {
      // TODO città dati mancanti
      // municipality: {
      //   id: data.statoEsteroVO?.key?.codice?.toString() || '',
      //   description: data.statoEsteroVO?.denominazione || '',
      //   province: data.statoEsteroVO?.codiceSiglaProvincia || '',
      // },
      state: {
        id: data.statoEsteroVO?.codiceIstat?.toString() || '',
        value: data.statoEsteroVO?.denominazione || '',
      },
      province: data.statoEsteroVO?.codiceSiglaProvincia,
    },
    isForeign: !Boolean(data.comuneAnprVO?.denominazione),
    // exceptionalBirthPlace: TODO mancante
    municipalityIdentifier: data.idDbLocaleRichiedente?.toString(),
    // istatCode: TODO idSchedaSoggettoComune -> idSchedaSoggettoComuneIstat
    anprIdentifier: data.idAnprRichiedente,
    note: data.note,
  };
};

// const transformCitizenship = (person: PersonData): Array<CitizenshipVM> | undefined => {
//   const originReturnData: Array<CitizenshipVM> | undefined = person.
//     ? person.cittadinanza.map((citizen) => ({
//         code: citizen.codiceStato || '',
//         citizenship: citizen.descrizioneStato || '',
//         note: citizen.noteStato || '',
//         validityDate: citizen.dataValidita
//           ? DateTime.fromISO(citizen.dataValidita).toFormat('dd/MM/yyyy')
//           : '',
//       }))
//     : undefined;

//   return originReturnData;
// };

export const transformRegisterOffice = (
  response: AxiosResponse<HttpResponse<PracticeData>>
): RegisterOfficeVM => {
  const data = response.data.payload || {};

  return {
    motivo: response.data.payload.motivo || '',
    disabledCancel: !!data.idWorkflowProcess && data.idWorkflowProcess !== '0',
    applicantIsAccountholder: data.flagRichiedente ? data.flagRichiedente === '0' : true,
    typeInitiative: (data.tipoIniziativa as TypeInitiative) || undefined,
    numberPracticeAnpr: data.numRichiestaAnpr || undefined,
    isAire: data.personaVO?.flagAire
      ? data.personaVO?.flagAire === '1' || data.personaVO?.flagAire === 'S'
      : false,
    stepBozza: data.stepBozza,
    statoPratica: data.codiceStatoPratica,
    codOperatorePratica: data.codOperatorePratica || '',
    accountholderData: data.personaVO
      ? {
          generality: data.personaVO ? transformGeneralityOffice(data.personaVO) : undefined,
          withSearch: Boolean(data.personaVO?.codFiscale), // se torna dal DB campi bloccati
          mother:
            data.personaVO?.cognomeMadre || data.personaVO?.nomeMadre
              ? {
                  surname: data.personaVO?.cognomeMadre?.split('/')[0] || '',
                  name:
                    data.personaVO?.cognomeMadre?.split('/')[1] || data.personaVO?.nomeMadre || '',
                }
              : undefined,
          father:
            data.personaVO?.cognomePadre || data.personaVO?.nomePadre
              ? {
                  surname: data.personaVO?.cognomePadre?.split('/')[0] || '',
                  name:
                    data.personaVO?.cognomePadre?.split('/')[1] || data.personaVO?.nomePadre || '',
                }
              : undefined,

          marriage:
            (data.personaVO?.cognomeConiugeUnito || data.personaVO?.nomeConiugeUnito) &&
            (data.personaVO?.codStatoCivile?.toString() === CivilStatusID.coniugato ||
              data.personaVO?.codStatoCivile?.toString() === CivilStatusID.divorziato)
              ? {
                  surname: data.personaVO?.cognomeConiugeUnito || '',
                  name: data.personaVO?.nomeConiugeUnito || '',
                }
              : undefined,
          civilUnion:
            (data.personaVO?.cognomeConiugeUnito || data.personaVO?.nomeConiugeUnito) &&
            (data.personaVO?.codStatoCivile?.toString() === CivilStatusID.unitoCivilmente ||
              data.personaVO?.codStatoCivile?.toString() ===
                CivilStatusID.scioglimentoUnitoCivilmente)
              ? {
                  surname: data.personaVO?.cognomeConiugeUnito || '',
                  name: data.personaVO?.nomeConiugeUnito || '',
                }
              : undefined,
          identificationDocument:
            data.flagRichiedente === '0'
              ? {
                  issuedDate: data.richiedenteVO?.dataRilascioDocumento
                    ? DateTime.fromISO(data.richiedenteVO?.dataRilascioDocumento)
                    : undefined,
                  documentNumber: data.richiedenteVO?.numeroDocumento || '',
                  municipality: data.richiedenteVO?.nomeComuneRilascioDocumento
                    ? {
                        provinceCode: data.richiedenteVO?.provinciaRilascioDocumento || '',
                        province: data.richiedenteVO?.provinciaRilascioDocumento || '',
                        id: data.richiedenteVO?.comuneRilascioDocumento || '',
                        description: data.richiedenteVO?.nomeComuneRilascioDocumento || '',
                      }
                    : undefined,
                  isForeign: Boolean(data.richiedenteVO?.luogoEsteroRilascioDocumento),
                  placeForeign: data.richiedenteVO?.luogoEsteroRilascioDocumento || '',
                }
              : undefined,
          citizenship: data?.personaVO?.cittadinanzaIntestatario?.codiceIstat
            ? [
                {
                  code: data?.personaVO?.cittadinanzaIntestatario?.codiceIstat || '',
                  citizenship: data?.personaVO?.cittadinanzaIntestatario?.denominazione || '',
                  validityDate: data?.personaVO?.cittadinanzaIntestatario?.dataFineValidita
                    ? DateTime.fromISO(
                        data?.personaVO.cittadinanzaIntestatario?.dataFineValidita
                      ).toFormat('dd/MM/yyyy')
                    : '',
                },
              ]
            : undefined,
          residenceOrigin: {
            irreperibile: data?.irreperibile,
            neverRegistered: data?.minorePreadozione,
            isForeign: !!data?.localita,
            origin: data?.comune
              ? {
                  id: data?.comune?.codiceIstat || '',
                  description: data?.comune?.nomeComune || '',
                  province: data?.comune?.siglaProvinciaIstat || '',
                }
              : undefined,
            originState: data?.localita
              ? {
                  id: data?.localita?.codiceStato || '',
                  value: data?.localita?.descrizioneStato || '',
                  province: data?.localita?.provinciaContea || '',
                }
              : undefined,
            originMunicipalityForeign: data?.localita
              ? {
                  id: data?.localita?.codiceStato || '',
                  description: data?.localita?.descrizioneLocalita || '',
                  province: data?.localita?.provinciaContea || '',
                }
              : undefined,
            previousOrigin: data?.comunePrecedente?.descrizione,
            previousOriginProvince: data?.comunePrecedente?.provincia,
            previousOriginSipoCode: data?.comunePrecedente?.codice,
            previousOriginCode: data?.comunePrecedente?.codice,
          },
        }
      : undefined,
    applicantData:
      data.flagRichiedente === '0' || !data.richiedenteVO
        ? undefined
        : {
            generality: data.richiedenteVO
              ? transformGeneralityRichiedenteOffice(data.richiedenteVO)
              : undefined,
            identificationDocument: {
              issuedDate: data.richiedenteVO?.dataRilascioDocumento
                ? DateTime.fromISO(data.richiedenteVO?.dataRilascioDocumento)
                : undefined,
              documentNumber: data.richiedenteVO?.numeroDocumento || '',
              municipality: data.richiedenteVO?.nomeComuneRilascioDocumento
                ? {
                    provinceCode: data.richiedenteVO?.provinciaRilascioDocumento || '',
                    province: data.richiedenteVO?.provinciaRilascioDocumento || '',
                    id: data.richiedenteVO?.comuneRilascioDocumento || '',
                    description: data.richiedenteVO?.nomeComuneRilascioDocumento || '',
                  }
                : undefined,
              isForeign: Boolean(data.richiedenteVO?.luogoEsteroRilascioDocumento),
              placeForeign: data.richiedenteVO?.luogoEsteroRilascioDocumento || '',
            },
            withSearch: Boolean(data.richiedenteVO?.codFiscaleRichiedente), // se torna dal DB campi bloccati
          },
  };
};
